<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import type { NodeFit, Usage } from '../model/fit'
import type { Segment } from './types'
import StackedBar from './StackedBar.vue'

/**
 * The node drawn as what it holds: one square per pod that fits, ghost squares for
 * pods its free resources could hold but its pod limit refuses, and a bar per
 * resource showing where the rest of the node went.
 */
const props = defineProps<{ fit: NodeFit }>()

const { t } = useI18n()
const format = useFormat()

/** Beyond this the squares get too small to read; the caption carries the count. */
const MAX_CELLS = 256

// Fixed order, matched to the chart tokens' validated order. Eviction only ever
// applies to memory, so on the CPU bar pods, DaemonSets and reserved stay neighbours.
const PARTS = [
  { key: 'pods', color: '--color-chart-1' },
  { key: 'daemonsets', color: '--color-chart-2' },
  { key: 'reserved', color: '--color-chart-3' },
  { key: 'eviction', color: '--color-chart-4' },
  { key: 'unused', color: 'none' },
] as const satisfies readonly { key: keyof Usage; color: string }[]

const blocked = computed(() =>
  props.fit.limitedBy === 'pods' ? Math.max(0, props.fit.podsByResources - props.fit.pods) : 0,
)
const shownPods = computed(() => Math.min(props.fit.pods, MAX_CELLS))
const shownGhosts = computed(() => Math.min(blocked.value, MAX_CELLS - shownPods.value))
const columns = computed(() =>
  Math.max(1, Math.ceil(Math.sqrt(shownPods.value + shownGhosts.value))),
)
const hidden = computed(() => props.fit.pods + blocked.value - shownPods.value - shownGhosts.value)

function segments(usage: Usage, show: (value: number) => string): Segment[] {
  return PARTS.map((part) => ({
    key: part.key,
    value: usage[part.key],
    color: part.color,
    title: `${t(`node.parts.${part.key}`)}: ${show(usage[part.key])}`,
  }))
}

const cpuSegments = computed(() => segments(props.fit.cpu, format.cpu))
const memorySegments = computed(() => segments(props.fit.memory, format.memory))

/** The legend lists only what this node has: no eviction row for zero, and so on. */
const rows = computed(() =>
  PARTS.filter((part) => props.fit.cpu[part.key] > 0 || props.fit.memory[part.key] > 0),
)
</script>

<template>
  <figure class="node">
    <figcaption class="visually-hidden">
      {{ t('node.caption', { name: fit.instance.name }) }}
    </figcaption>

    <div class="node__frame">
      <div
        v-if="shownPods + shownGhosts > 0"
        class="node__pods"
        :style="{
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          maxWidth: `${columns * 2.25}rem`,
        }"
        aria-hidden="true"
      >
        <span v-for="index in shownPods" :key="`pod-${index}`" class="node__pod" />
        <span
          v-for="index in shownGhosts"
          :key="`ghost-${index}`"
          class="node__pod node__pod--ghost"
        />
      </div>
      <p v-else class="node__empty">{{ t('node.empty') }}</p>

      <p class="node__count">
        {{ t('node.pods', { count: format.count(fit.pods) }, fit.pods) }}
        <template v-if="hidden > 0">
          · {{ t('node.notDrawn', { count: format.count(hidden) }) }}</template
        >
      </p>
      <p v-if="blocked > 0" class="node__blocked">
        <span class="node__swatch node__swatch--ghost" aria-hidden="true" />
        {{
          t(
            'node.blocked',
            { count: format.count(blocked), max: format.count(fit.maxPods) },
            blocked,
          )
        }}
      </p>
    </div>

    <div class="node__bars">
      <div class="node__bar">
        <span class="node__bar-label"
          >{{ t('node.cpu') }} · {{ format.cpu(fit.cpu.capacity) }}</span
        >
        <StackedBar :segments="cpuSegments" />
      </div>
      <div class="node__bar">
        <span class="node__bar-label"
          >{{ t('node.memory') }} · {{ format.memory(fit.memory.capacity) }}</span
        >
        <StackedBar :segments="memorySegments" />
      </div>
    </div>

    <table class="node__legend">
      <caption class="visually-hidden">
        {{
          t('node.legendCaption')
        }}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            <span class="visually-hidden">{{ t('node.part') }}</span>
          </th>
          <th scope="col">{{ t('node.cpu') }}</th>
          <th scope="col">{{ t('node.memory') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="part in rows" :key="part.key">
          <th scope="row">
            <span
              class="node__swatch"
              :class="{ 'node__swatch--empty': part.color === 'none' }"
              :style="part.color === 'none' ? undefined : { background: `var(${part.color})` }"
              aria-hidden="true"
            />
            {{ t(`node.parts.${part.key}`) }}
          </th>
          <td class="mono">{{ fit.cpu[part.key] > 0 ? format.cpu(fit.cpu[part.key]) : '—' }}</td>
          <td class="mono">
            {{ fit.memory[part.key] > 0 ? format.memory(fit.memory[part.key]) : '—' }}
          </td>
        </tr>
      </tbody>
    </table>
  </figure>
</template>

<style scoped>
.node {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin: 0;
}

.node__frame {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.node__pods {
  display: grid;
  gap: 2px;
  width: 100%;
}

.node__pod {
  aspect-ratio: 1;
  border-radius: 2px;
  background: var(--color-chart-1);
}

/* Room the pod limit refuses: the same hue, faint, so it reads as "could be". */
.node__pod--ghost {
  background: color-mix(in srgb, var(--color-chart-1) 22%, transparent);
}

.node__empty,
.node__count,
.node__blocked {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  text-align: center;
}

.node__count {
  color: var(--color-text);
  font-weight: 600;
}

.node__bars {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.node__bar {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.node__bar-label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.node__legend {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.node__legend th,
.node__legend td {
  padding: var(--space-1) var(--space-2);
  text-align: right;
  font-weight: 400;
}

.node__legend thead th {
  color: var(--color-text-muted);
}

.node__legend tbody th {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  text-align: left;
}

.node__legend tbody tr + tr {
  border-top: 1px solid var(--color-border);
}

.node__swatch {
  display: inline-block;
  flex: none;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 2px;
  vertical-align: middle;
}

.node__swatch--empty {
  background: var(--color-surface-sunken);
  box-shadow: inset 0 0 0 1px var(--color-border-strong);
}

.node__swatch--ghost {
  background: color-mix(in srgb, var(--color-chart-1) 22%, transparent);
}
</style>
